/**
 * DocumentViewerModal — the shared in-page document viewer for Origin.
 * Entity-agnostic: give it the document list from useCrmDocuments, an index,
 * and the hook's getUrl, and it renders any uploadable type over the current
 * page (works stacked above drawers — same nested-portal behavior as the
 * delete AlertDialog).
 *
 *   <DocumentViewerModal
 *     isOpen={viewerIndex !== null}
 *     onClose={() => setViewerIndex(null)}
 *     documents={documents}
 *     index={viewerIndex ?? 0}
 *     onIndexChange={setViewerIndex}
 *     getUrl={getUrl}          // (documentId, 'inline'|'download') from useCrmDocuments
 *   />
 *
 * The file bytes are fetched ONCE per document via a short-lived signed URL
 * (audited as a 'view'), then every renderer works from memory — an expired
 * URL can never break an open viewer. Renderers are lazy chunks; the modal
 * itself adds almost nothing to the main bundle.
 */
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  Button,
  Center,
  Spinner,
  Icon,
  Tooltip,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiAlertTriangle,
} from 'react-icons/fi';
import {
  getViewerType,
  getCategoryMeta,
  formatFileSize,
} from '../../../constants/documentConstants';
import FallbackViewer from './viewers/FallbackViewer';

const PdfViewer = lazy(() => import('./viewers/PdfViewer'));
const ImageViewer = lazy(() => import('./viewers/ImageViewer'));
const DocxViewer = lazy(() => import('./viewers/DocxViewer'));
const SheetViewer = lazy(() => import('./viewers/SheetViewer'));
const TextViewer = lazy(() => import('./viewers/TextViewer'));

const rendererFallback = (
  <Center flex={1}>
    <Spinner color="blue.400" />
  </Center>
);

export default function DocumentViewerModal({
  isOpen,
  onClose,
  documents = [],
  index = 0,
  onIndexChange,
  getUrl,
}) {
  const toast = useToast();
  const doc = documents[index] || null;
  const viewerType = doc ? getViewerType(doc.mimeType, doc.fileName) : 'none';

  // { status: 'loading'|'ready'|'error', buffer?, error? }
  const [fetchState, setFetchState] = useState({ status: 'loading' });
  const [retryNonce, setRetryNonce] = useState(0);
  const [downloading, setDownloading] = useState(false);
  // Ignore late responses after the user switched documents or closed.
  const fetchSeqRef = useRef(0);

  const headerBorder = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    const seq = ++fetchSeqRef.current;
    if (!isOpen || !doc) return;
    if (viewerType === 'none') {
      // FallbackViewer needs no bytes — don't fetch (and don't log a view).
      setFetchState({ status: 'ready', buffer: null });
      return;
    }
    setFetchState({ status: 'loading' });
    (async () => {
      try {
        const res = await getUrl(doc.id, 'inline');
        if (!res?.success || !res.url) {
          throw new Error(res?.error || 'Could not get file link');
        }
        const response = await fetch(res.url);
        if (!response.ok) {
          throw new Error(`File fetch failed (${response.status})`);
        }
        const buffer = await response.arrayBuffer();
        if (fetchSeqRef.current === seq) {
          setFetchState({ status: 'ready', buffer });
        }
      } catch (err) {
        console.error(`Document preview failed (${doc.fileName}):`, err);
        if (fetchSeqRef.current === seq) {
          setFetchState({ status: 'error', error: err.message || 'Preview failed' });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, doc?.id, viewerType, retryNonce]);

  const handleDownload = async () => {
    if (!doc) return;
    setDownloading(true);
    const res = await getUrl(doc.id, 'download');
    setDownloading(false);
    if (res?.success && res.url) {
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      toast({ title: 'Download failed', description: res?.error, status: 'error', duration: 5000 });
    }
  };

  const handleOpenInNewTab = async () => {
    if (!doc) return;
    // Open synchronously so popup blockers allow it, then attach the URL.
    const win = window.open('about:blank', '_blank');
    const res = await getUrl(doc.id, 'inline');
    if (res?.success && res.url) {
      if (win) {
        win.location.href = res.url;
      } else {
        window.open(res.url, '_blank');
      }
    } else {
      if (win) win.close();
      toast({
        title: 'Couldn\u2019t open file',
        description: res?.error,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const category = doc ? getCategoryMeta(doc.category) : null;
  const canOpenInTab = viewerType === 'pdf' || viewerType === 'image';
  const showNav = documents.length > 1;

  const renderBody = () => {
    if (!doc) {
      return (
        <Center flex={1}>
          <Text fontSize="sm" color="gray.500">
            Document unavailable.
          </Text>
        </Center>
      );
    }
    if (viewerType === 'none') {
      return <FallbackViewer doc={doc} onDownload={handleDownload} downloading={downloading} />;
    }
    if (fetchState.status === 'loading') {
      return (
        <Center flex={1}>
          <VStack spacing={3}>
            <Spinner color="blue.400" />
            <Text fontSize="sm" color="gray.500">
              Loading {doc.fileName}&hellip;
            </Text>
          </VStack>
        </Center>
      );
    }
    if (fetchState.status === 'error') {
      return (
        <Center flex={1} px={6}>
          <VStack spacing={3} textAlign="center" maxW="sm">
            <Icon as={FiAlertTriangle} boxSize={8} color="orange.400" />
            <Text fontWeight="medium">Couldn&rsquo;t load the preview</Text>
            <Text fontSize="sm" color="gray.500">
              {fetchState.error}
            </Text>
            <HStack>
              <Button size="sm" variant="outline" onClick={() => setRetryNonce((n) => n + 1)}>
                Retry
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiDownload />}
                onClick={handleDownload}
                isLoading={downloading}
              >
                Download
              </Button>
            </HStack>
          </VStack>
        </Center>
      );
    }

    const { buffer } = fetchState;
    return (
      <Suspense fallback={rendererFallback}>
        {viewerType === 'pdf' && <PdfViewer buffer={buffer} />}
        {viewerType === 'image' && (
          <ImageViewer buffer={buffer} mimeType={doc.mimeType} fileName={doc.fileName} />
        )}
        {viewerType === 'docx' && <DocxViewer buffer={buffer} />}
        {viewerType === 'sheet' && <SheetViewer buffer={buffer} />}
        {viewerType === 'text' && <TextViewer buffer={buffer} />}
      </Suspense>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent h="90vh" maxH="90vh" overflow="hidden">
        <ModalHeader
          py={3}
          pr={14}
          borderBottomWidth="1px"
          borderColor={headerBorder}
        >
          <HStack spacing={4} align="center">
            <Box flex={1} minW={0}>
              <Tooltip label={doc?.fileName} openDelay={500}>
                <Text fontSize="md" fontWeight="semibold" noOfLines={1}>
                  {doc?.fileName || 'Document'}
                </Text>
              </Tooltip>
              <HStack spacing={2} mt={0.5}>
                {category && (
                  <Badge colorScheme={category.color} fontSize="0.65rem">
                    {category.label}
                  </Badge>
                )}
                <Text fontSize="xs" color="gray.500" fontWeight="normal">
                  {formatFileSize(doc?.fileSize)}
                </Text>
              </HStack>
            </Box>

            {showNav && (
              <HStack spacing={1} flexShrink={0}>
                <IconButton
                  aria-label="Previous document"
                  icon={<FiChevronLeft />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onIndexChange?.(index - 1)}
                  isDisabled={index <= 0}
                />
                <Text fontSize="xs" color="gray.500" fontWeight="normal" whiteSpace="nowrap">
                  {index + 1} of {documents.length}
                </Text>
                <IconButton
                  aria-label="Next document"
                  icon={<FiChevronRight />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onIndexChange?.(index + 1)}
                  isDisabled={index >= documents.length - 1}
                />
              </HStack>
            )}

            <HStack spacing={1} flexShrink={0}>
              {canOpenInTab && (
                <Tooltip label="Open in new tab">
                  <IconButton
                    aria-label="Open in new tab"
                    icon={<FiExternalLink />}
                    size="sm"
                    variant="ghost"
                    onClick={handleOpenInNewTab}
                  />
                </Tooltip>
              )}
              <Tooltip label="Download">
                <IconButton
                  aria-label="Download"
                  icon={<FiDownload />}
                  size="sm"
                  variant="ghost"
                  isLoading={downloading}
                  onClick={handleDownload}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} />
        <ModalBody p={0} display="flex" flexDirection="column" overflow="hidden">
          {renderBody()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
